import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';

import { TranslatedRouter } from '@core-services';
import { Path } from '@shared-enums';
import {
  togglePaymentCancelAction,
  togglePaymentErrorAction,
  togglePaymentSuccessfulAction,
} from '@store-shopping-cart-actions';
import {
  paymentCancelSelector,
  paymentErrorSelector,
  paymentSuccessfulSelector,
} from '@store-shopping-cart-selectors';
import { filter, Observable, switchMap } from 'rxjs';
import {
  DialogPaymentCancelComponent,
  DialogPaymentErrorComponent,
  DialogPaymentSuccessfulComponent,
} from '..';

@Component({
  selector: 'app-payment-successful',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, TranslateModule],
  templateUrl: './payment-successful.component.html',
  styleUrl: './payment-successful.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentSuccessfulComponent implements OnInit {
  //#region Dependencies

  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private router = inject(TranslatedRouter);

  //#endregion

  //#region Lifecycle Hooks

  ngOnInit(): void {
    this.initPaymentSuccessSubscription();
    this.initPaymentErrorSubscription();
    this.initPaymentCancelSubscription();
  }

  //#endregion

  //#region Init

  private initPaymentSuccessSubscription(): void {
    this.store
      .select(paymentSuccessfulSelector)
      .pipe(
        filter(showSuccessMessage => showSuccessMessage === true),
        switchMap(this.showSuccessDialog.bind(this)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(this.handleSuccessDialogClosed.bind(this));
  }

  private initPaymentErrorSubscription(): void {
    this.store
      .select(paymentErrorSelector)
      .pipe(
        filter(showSuccessMessage => showSuccessMessage === true),
        switchMap(this.showErrorDialog.bind(this)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(this.handleErrorDialogClosed.bind(this));
  }

  private initPaymentCancelSubscription(): void {
    this.store
      .select(paymentCancelSelector)
      .pipe(
        filter(showSuccessMessage => showSuccessMessage === true),
        switchMap(this.showCancelDialog.bind(this)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(this.handleCancelDialogClosed.bind(this));
  }

  //#endregion

  //#region Utilities

  private showSuccessDialog(): Observable<unknown> {
    return this.dialog
      .open(DialogPaymentSuccessfulComponent, {
        disableClose: true,
      })
      .afterClosed();
  }

  private showErrorDialog(): Observable<unknown> {
    return this.dialog
      .open(DialogPaymentErrorComponent, {
        disableClose: true,
      })
      .afterClosed();
  }

  private showCancelDialog(): Observable<unknown> {
    return this.dialog
      .open(DialogPaymentCancelComponent, {
        disableClose: true,
      })
      .afterClosed();
  }

  //#endregion

  //#region Handlers

  private handleSuccessDialogClosed(): void {
    this.store.dispatch(togglePaymentSuccessfulAction({ showMessage: false }));
    this.router.navigateByUrl(Path.Root);
  }

  private handleErrorDialogClosed(): void {
    this.store.dispatch(togglePaymentErrorAction({ showMessage: false }));
    this.router.navigateByUrl(Path.Root);
  }

  private handleCancelDialogClosed(): void {
    this.store.dispatch(togglePaymentCancelAction({ showMessage: false }));
    this.router.navigateByUrl(Path.Root);
  }

  //#endregion
}
